import { useState } from 'react';
import { Project, EnvVar } from '@vault/shared';
import { Plus, FileText, Loader2 } from 'lucide-react';
import { useEnvVars } from '../hooks/useEnvVars';
import { formatEnvVarsForDisplay } from '../utils/clipboard';
import { apiClient } from '../services/api';
import EnvVarEditor from './EnvVarEditor';
import EnvVarDisplay from './EnvVarDisplay';
import { EditEnvVarModal } from './EditEnvVarModal';
import ConfirmDialog from './ConfirmDialog';
import toast from 'react-hot-toast';

interface ProjectViewProps {
  project: Project;
}

export default function ProjectView({ project }: ProjectViewProps) {
  const [showEditor, setShowEditor] = useState(false);
  const [editingEnvVar, setEditingEnvVar] = useState<EnvVar | null>(null);
  const [deletingEnvVar, setDeletingEnvVar] = useState<EnvVar | null>(null);
  const { envVars, loading, bulkUpsertEnvVars, refetch } = useEnvVars(project.id);

  const handleSaveEnvVars = async (content: string) => {
    await bulkUpsertEnvVars(content);
    setShowEditor(false);
  };



  const handleEditEnvVar = (envVar: EnvVar) => {
    setEditingEnvVar(envVar);
  };

  const handleSaveEditEnvVar = async (envVar: EnvVar, newKey: string, newValue: string) => {
    try {
      await apiClient.updateEnvVar(envVar.id, { key: newKey, value: newValue });
      await refetch();
      setEditingEnvVar(null);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteEnvVar = (envVar: EnvVar) => {
    setDeletingEnvVar(envVar);
  };

  const handleConfirmDelete = async () => {
    if (!deletingEnvVar) return;

    try {
      await apiClient.deleteEnvVar(deletingEnvVar.id);
      await refetch();
      toast.success(`Variable ${deletingEnvVar.key} deleted successfully!`);
      setDeletingEnvVar(null);
    } catch (error) {
      toast.error('Failed to delete variable');
    }
  };

  return (
    <div className="card h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {envVars.length} environment variable{envVars.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowEditor(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Variables
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : envVars.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No environment variables
              </h3>
              <p className="text-gray-500 mb-4">
                Add environment variables to get started with this project.
              </p>
              <button
                onClick={() => setShowEditor(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Variables
              </button>
            </div>
          </div>
        ) : (
          <EnvVarDisplay 
            envVars={envVars} 
            onEditEnvVar={handleEditEnvVar}
            onDeleteEnvVar={handleDeleteEnvVar}
          />
        )}
      </div>

      {/* Environment Variables Editor Modal */}
      <EnvVarEditor
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleSaveEnvVars}
        initialContent={formatEnvVarsForDisplay(envVars)}
      />

      {/* Edit Environment Variable Modal */}
      <EditEnvVarModal
        envVar={editingEnvVar}
        isOpen={!!editingEnvVar}
        onClose={() => setEditingEnvVar(null)}
        onSave={handleSaveEditEnvVar}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deletingEnvVar}
        onClose={() => setDeletingEnvVar(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Environment Variable"
        message={
          deletingEnvVar
            ? `Are you sure you want to delete "${deletingEnvVar.key}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}